// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";
import { useNavigate, useParams } from "react-router-dom";

// material styles
import { Grid, Button, TextField } from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// request methods
import {
  editWorkSampleRequest,
  deleteWorkSampleByIdRequest,
  getWorkSampleCategoryRequest,
  getWorkSampleByIdRequest,
} from "api/requests/work_sample";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";
import DeleteModal from "components/shared/Modal/Delete";

// helper
import Toast from "helper/toast";

// form validate
import { workSampleSchema, validateImg } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./editWorkSample.style";

// react icons
import { MdDelete } from "react-icons/md";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import BackButton from "components/shared/BackButton";

export default function EditWorkSample() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const navigate = useNavigate();
  const { work_sample_id } = useParams();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(workSampleSchema),
  });

  // editor value
  const [text, setText] = useState("");

  // text editor value handler method
  const handleChangeText = (text) => setText(text);

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  //handle delete modal
  const handelShowModal = () => setShowDeleteModal((state) => !state);

  // delete worksample by id
  const handleDeleteItem = useCallback(
    async (id) => {
      const res = await request({
        url: () => deleteWorkSampleByIdRequest(id),
      });
      if (res) {
        Toast({ type: "success", title: "حذف نمونه کار " });
        navigate("/work_samples");
      }
      handelShowModal();
    },
    [setLoading]
  );

  // upload image state
  const [workSampleImage, setWorkSampleImage] = useState({
    url: null,
    isChanged: false,
  });

  // // select option state
  // const [categoryList, setCategoryList] = useState([]);
  // const [selectedCategory, setSelectedCategory] = useState(null);

  // edit workSample
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!workSampleImage.url)
      return Toast({ type: "empty", title: "فیلد عکس " });
    const res = await request({
      url: () =>
        editWorkSampleRequest(
          {
            id: work_sample_id,
            ...data,
            description: text,
            // category_id: data.category,
            image: workSampleImage,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    if (res) {
      Toast({ type: "success", title: "ویرایش نمونه کار" });
      navigate("/work_samples");
    }
  };

  // uploaded image handler method
  const handleChangeWorkSampleImage = (val) => {
    validateImg(val);
    setWorkSampleImage({ url: val, isChanged: true });
  };

  // get category
  const getWorkSampleData = useCallback(async () => {
    // const categoryRes = await request({
    //   url: getWorkSampleCategoryRequest,
    //   values: {
    //     skip: 0,
    //     limit: 100,
    //     search: null,
    //   },
    //   isMulty: true,
    // });
    // setLoading(false);
    // if (categoryRes) {
    //   let transformedData = categoryRes.data?.data.map((items) => ({
    //     id: items.id,
    //     title: items.title,
    //   }));
    //   setCategoryList(transformedData);
    // }
    setLoading(true);
    const workSampleRes = await request({
      url: () => getWorkSampleByIdRequest({ id: work_sample_id }),
    });
    if (workSampleRes) {
      let data = workSampleRes.data;
      setText(data.description);
      if (data.image) setWorkSampleImage({ url: data?.image, isWeb: true });
      reset({
        title: data.title,
        link: data?.link ?? "",
        alt: data?.image_details?.alt,
        meta_description: data?.seo_options?.meta_description,
        keyword: data?.seo_options?.keyword,
      });
      // data?.category_id &&
      //   reset({
      //     title: data.title,
      //     link: data.link,
      //     category: data.category_id,
      //   });
    }
    setLoading(false);
  }, [request]);

  useEffect(() => {
    getWorkSampleData();
  }, [getWorkSampleData]);

  return (
    <DrawerCM title="ویرایش نمونه کار">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <Grid item mt={3} xs={12}>
            <BackButton Route="/work_samples" />
          </Grid>
          <StyledStack>
            <StyledTitle>ویرایش نمونه کار</StyledTitle>
            <Grid container>
              <Grid item md={6} xs={12} flexDirection="column">
                <Grid item xs={12} p={1.2}>
                  <TextField
                    label="عنوان"
                    variant="outlined"
                    fullWidth
                    id="title"
                    {...register("title")}
                    error={Boolean(errors.title)}
                    helperText={errors.title?.message}
                  />
                </Grid>
                <Grid item xs={12} p={1.2}>
                  <TextField
                    label="لینک"
                    variant="outlined"
                    fullWidth
                    id="link"
                    {...register("link")}
                    error={Boolean(errors.link)}
                    helperText={errors.link?.message}
                  />
                </Grid>
                {/* <Grid item xs={12} p={1.2}>
                  <ReactHookFormSelect
                    label="سردسته"
                    control={control}
                    title="سردسته"
                    id="category"
                    {...register("category")}
                    error={Boolean(errors.category)}
                    helperText={errors.category?.message}
                    onChange={(value) => setSelectedCategory(value)}
                  >
                    {categoryList?.map((item, index) => (
                      <StyledMenuItem key={index} value={item.id}>
                        {item.title}
                      </StyledMenuItem>
                    ))}
                  </ReactHookFormSelect>
                </Grid> */}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {workSampleImage.url ? (
                  <Upload
                    isWeb={workSampleImage.isWeb}
                    isLocal={!workSampleImage.isWeb}
                    file={workSampleImage.url}
                    refillable
                    type="add-work-sample"
                    onChange={handleChangeWorkSampleImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={workSampleImage.isChanged}
                    file={workSampleImage.url}
                    refillable
                    type="add-work-sample"
                    onChange={handleChangeWorkSampleImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label=" متن جایگزین"
                  variant="outlined"
                  fullWidth
                  id="alt"
                  {...register("alt")}
                  error={Boolean(errors.alt)}
                  helperText={errors.alt?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label="کلمه کلیدی"
                  variant="outlined"
                  fullWidth
                  id="keyword"
                  {...register("keyword")}
                  error={Boolean(errors.keyword)}
                  helperText={errors.keyword?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <TextField
                  label="فراتوضیحات"
                  variant="outlined"
                  fullWidth
                  id="meta_description"
                  {...register("meta_description")}
                  error={Boolean(errors.meta_description)}
                  helperText={errors.meta_description?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                {text && (
                  <Editor
                    enableToolbar={true}
                    onChange={handleChangeText}
                    defaultValue={text}
                    setDefaultStyle="font-family: IranSans; height: 40vh;"
                    setOptions={{
                      buttonList: buttonList.complex,
                      font: [
                        "IranSans",
                        "IranSansBold",
                        "Arial",
                        "Impact",
                        "Tahoma",
                      ],
                    }}
                  />
                )}
              </Grid>
              <ButtonContainer>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1, mr: 2 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
                <Button
                  startIcon={<MdDelete />}
                  variant="contained"
                  color="error"
                  sx={{ mt: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handelShowModal();
                  }}
                >
                  حذف
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
          <DeleteModal
            show={showDeleteModal}
            onClose={handelShowModal}
            onDelete={handleDeleteItem.bind(null, work_sample_id)}
          />
        </Grid>
      )}
    </DrawerCM>
  );
}
